// FUNCTIONALITY-115B — payment receipt upload client with timeout + idempotency.
import { storeV2AuthHeaders } from "./store-v2-auth-headers-v2.js";

export interface PaymentReceiptUploadResult {
  ok: boolean;
  accepted?: boolean;
  receipt_id?: string;
  event_id?: string;
  manager_delivery_status?: string;
  manager_message_id?: string | null;
  retryable?: boolean;
  duplicate?: boolean;
  deduped?: boolean;
  found?: boolean;
  user_message?: string;
  status?: string;
  message_uk?: string;
  error?: string;
  required_binding?: string;
}

/** receipt proof != paid. BANK_IBAN Autoclient reconciliation remains source of truth. */

/** Stable per order + file identity so retries / double-taps dedupe (no Date.now). */
export function makeReceiptIdempotencyKey(orderId: string, file: File): string {
  const safe = file.name.replace(/[^\w.\-]+/g, "_").slice(0, 64);
  return `rcpt-${orderId}-${safe}-${file.size}-${file.lastModified}`;
}

export async function uploadPaymentReceiptV2(input: {
  orderId: string;
  sessionKey: string;
  file: File;
  paymentComment?: string | null;
  paymentUrl?: string | null;
  timeoutMs?: number;
  idempotencyKey: string;
}): Promise<PaymentReceiptUploadResult> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), input.timeoutMs ?? 25000);
  const form = new FormData();
  form.set("session_key", input.sessionKey);
  form.set("idempotency_key", input.idempotencyKey);
  if (input.paymentComment) form.set("payment_comment", input.paymentComment);
  if (input.paymentUrl) form.set("payment_url", input.paymentUrl);
  form.set("receipt", input.file, input.file.name);
  try {
    const res = await fetch(
      `/api/store-v2/payment-receipt?order_id=${encodeURIComponent(input.orderId)}`,
      { method: "POST", body: form, signal: controller.signal, headers: storeV2AuthHeaders() },
    );
    const json = (await res.json().catch(() => ({}))) as PaymentReceiptUploadResult;
    if (!res.ok || json.ok === false) {
      return {
        ok: false,
        accepted: false,
        retryable: true,
        error: json.error ?? "upload_failed",
        ...(json.status ? { status: json.status } : {}),
        message_uk:
          json.user_message ??
          json.message_uk ??
          "Не вдалося надіслати файл. Спробуйте ще раз або надішліть скрін у чат.",
        user_message:
          json.user_message ??
          json.message_uk ??
          "Не вдалося надіслати файл. Спробуйте ще раз або надішліть скрін у чат.",
        ...(json.required_binding ? { required_binding: json.required_binding } : {}),
      };
    }
    return {
      ok: true,
      accepted: json.accepted !== false,
      ...(json.receipt_id ? { receipt_id: json.receipt_id } : {}),
      ...(json.event_id ? { event_id: json.event_id } : {}),
      duplicate: json.duplicate === true,
      deduped: json.deduped === true,
      manager_delivery_status: json.manager_delivery_status ?? "pending",
      manager_message_id: json.manager_message_id ?? null,
      retryable: false,
      user_message: json.user_message ?? json.message_uk ?? "Квитанцію надіслано менеджеру",
      message_uk: json.user_message ?? json.message_uk ?? "Квитанцію надіслано менеджеру",
      status: json.status ?? "accepted",
    };
  } catch (error) {
    const aborted = error instanceof DOMException && error.name === "AbortError";
    return {
      ok: false,
      accepted: false,
      retryable: true,
      error: aborted ? "timeout" : "network_error",
      message_uk: aborted
        ? "Час очікування вичерпано. Спробуйте ще раз — файл збережено локально."
        : "Не вдалося надіслати файл. Спробуйте ще раз.",
      user_message: aborted
        ? "Час очікування вичерпано. Спробуйте ще раз — файл збережено локально."
        : "Не вдалося надіслати файл. Спробуйте ще раз.",
    };
  } finally {
    window.clearTimeout(timer);
  }
}

export async function getPaymentReceiptStatusV2(input: {
  orderId: string;
  sessionKey: string;
  idempotencyKey: string;
}): Promise<PaymentReceiptUploadResult> {
  try {
    const params = new URLSearchParams({
      order_id: input.orderId,
      session_key: input.sessionKey,
      idempotency_key: input.idempotencyKey,
    });
    const res = await fetch(`/api/store-v2/payment-receipt?${params.toString()}`, {
      headers: storeV2AuthHeaders(),
      cache: "no-store",
    });
    const json = (await res.json().catch(() => ({}))) as PaymentReceiptUploadResult;
    return {
      ...json,
      ok: res.ok && json.ok !== false,
      accepted: json.accepted === true,
      retryable: json.retryable !== false,
    };
  } catch {
    return { ok: false, accepted: false, retryable: true, error: "status_unavailable" };
  }
}
